/**
 * Helpers for guest voice notes via MediaRecorder.
 * iOS (Safari/Chrome) only records AAC in an MP4 container; Android prefers WebM/Opus.
 */

const isAppleDevice = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS reports as Macintosh with touch
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
};

const APPLE_MIME_TYPES = [
  "audio/mp4",
  "audio/aac",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

const DEFAULT_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/aac",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

/** Best MediaRecorder mime type for the current browser, or "". */
export const pickRecorderMimeType = () => {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return "";
  }
  const preferred = isAppleDevice() ? APPLE_MIME_TYPES : DEFAULT_MIME_TYPES;
  return preferred.find((type) => MediaRecorder.isTypeSupported(type)) || "";
};

const extensionForMime = (mime = "") => {
  const base = String(mime).split(";")[0].trim().toLowerCase();
  if (base.includes("mp4") || base.includes("aac") || base.includes("m4a")) {
    return "m4a";
  }
  if (base.includes("ogg")) return "ogg";
  if (base.includes("mpeg") || base.includes("mp3")) return "mp3";
  if (base.includes("wav")) return "wav";
  return "webm";
};

/** Normalize mime for upload/playback (strip codecs; use audio/mp4 for .m4a). */
export const normalizeAudioMime = (mime = "") => {
  const base = String(mime).split(";")[0].trim().toLowerCase() || "audio/webm";
  if (base.includes("mp4") || base.includes("aac") || base.includes("m4a")) {
    return "audio/mp4";
  }
  return base;
};

/** Build a voice File from MediaRecorder chunks. */
export const voiceFileFromChunks = (chunks, mimeType) => {
  const fileType = normalizeAudioMime(mimeType);
  const extension = extensionForMime(fileType);
  const blob = new Blob(chunks, { type: fileType });
  return new File([blob], `voice-${Date.now()}.${extension}`, {
    type: fileType,
  });
};
